<template>
  <section class="case-study-editor-main rounded">
    <!-- Image Section uploader modal -->
    <modal v-if="editable" name="file_section_upload_modal">
      <div class="mt-3 mr-4 ml-4">
        <h2>Upload image file</h2>
        <vue2Dropzone
          id="dropzone"
          ref="fileUploader"
          :options="imageDropzoneOptions"
          @vdropzone-success="uploadSuccess"
        >
        </vue2Dropzone>
      </div>
    </modal>

    <!-- Testimonial select modal -->
    <modal v-if="editable" name="testimonial_select_modal" class="testimonial-select">
      <div class="mt-4 ml-3 mr-3" v-if="testimonials">
        <h2>Select a testimonial to use</h2>
        <ul class="list-group" v-for="testimonial in testimonials" :key="testimonial.response_id">
          <li role="button" class="list-group-item" @click="createTestimonialSection(testimonial)">
            {{ testimonial.text }}
          </li>
        </ul>
      </div>
      <div v-else>No testimonials available for this respondent</div>
    </modal>

    <!-- Header  -->
    <div
      class="main-header d-flex justify-content-between rounded-top"
      :style="{ background: headerBackgroundColor }"
    >
      <div class="main-header__summary d-flex flex-column">
        <div class="main-header__summary__logo">
          <figure class="mb-0" v-html="caseStudy.svg_logo"></figure>
        </div>

        <div>
          <span
            :contenteditable="editableText"
            name="title"
            class="main-header__summary__title font-weight-bold"
            @blur="writeField"
            @keydown.enter="writeField"
          >
            {{ caseStudy.title || 'Add a title' }}
          </span>
        </div>

        <div>
          <span
            :contenteditable="editableText"
            name="description"
            class="main-header__summary__description"
            @blur="writeField"
            @keydown.enter="writeField"
          >
            {{ caseStudy.description || 'Add a description' }}
          </span>
        </div>

        <a
          class="d-flex align-items-center justify-content-center main-header__summary__cta font-weight-bold rounded text-decoration-none"
          :style="{ backgroundColor: accentColor }"
          :href="caseStudy.learn_more_url"
          v-if="caseStudy.learn_more_url"
          target="_blank"
          >Learn More</a
        >
      </div>

      <div v-if="caseStudy.show_header_image" class="main-header__profile-img">
        <CustomerHeader :case-study="caseStudy" :editable="editable"></CustomerHeader>
      </div>
    </div>

    <div v-if="editable" class="main-header__add-section d-flex justify-content-center">
      <div>
        <b-dropdown
          text="+ Add a section"
          variant="primary"
          class="m-md-2"
          no-caret
          v-if="editable"
        >
          <span class="ml-4 font-weight-bold">Add a section</span>

          <b-dropdown-item class="mt-2" @click="addSection('text')">
            <b-icon icon="textarea-t" class="mr-2" />
            <span>Text Section</span>
          </b-dropdown-item>
          <b-dropdown-item @click="addSection('image')">
            <b-icon icon="image" class="mr-2" />
            <span>Image</span>
          </b-dropdown-item>
          <b-dropdown-item @click="addSection('video')">
            <b-icon icon="camera-video" class="mr-2" />
            <span>Video</span>
          </b-dropdown-item>

          <div class="mt-1 mb-2 border"></div>

          <b-dropdown-item @click="addSection('key_statistics')">
            <b-icon icon="bar-chart-fill" class="mr-2" />
            <span>Key statistics</span>
          </b-dropdown-item>
          <b-dropdown-item :disabled="hasSnapshot" @click="addSection('survey_snapshot')">
            <b-icon icon="list-check" class="mr-2" />
            <span>Survey Snapshot</span>
          </b-dropdown-item>
          <b-dropdown-item :disabled="!hasTestimonials" @click="addSection('testimonial')">
            <b-icon icon="chat-quote" class="mr-2" />
            <span>Testimonial</span>
          </b-dropdown-item>
        </b-dropdown>
      </div>
    </div>

    <!-- Body -->
    <section
      class="main-body bg-white d-flex rounded-bottom border"
      :class="{ 'overflowed-top': editable }"
    >
      <div v-if="caseStudy.show_profile" class="main-body__left-summary">
        <!-- Left side Company summary -->
        <SummaryCard :case-study="caseStudy" :editable="editable"></SummaryCard>
      </div>

      <div class="main-body__section-wrapper">
        <CaseStudySectionGuts
          :sections="caseStudy.sections"
          :accent-color="accentColor"
          :editable="editable"
          :case-study-id="caseStudy.id"
          :recipient-id="caseStudy.recipient_id"
        />
      </div>
    </section>
  </section>
</template>
<script>
import vue2Dropzone from 'vue2-dropzone'
import { getCsrfToken } from '@lib/csrf'
import AssetSourceMixin from '@app/mixins/asset_source'
import BrandedAssetMixin from '@app/mixins/branded_asset.ts'
import { generateShareLink } from '@lib/share_link'
import { useCaseStudyStore } from '@app/stores/CaseStudyStore'
import SummaryCard from '@app/pages/case_studies/SummaryCard.vue'
import CustomerHeader from '@app/pages/case_studies/CustomerHeader.vue'
import CaseStudySectionGuts from '@app/pages/case_studies/CaseStudySectionGuts.vue'
import { useTestimonialForRecipientStore } from '@app/stores/TestimonialForRecipientStore'

export default {
  name: 'CaseStudy',
  components: {
    vue2Dropzone,
    SummaryCard,
    CaseStudySectionGuts,
    CustomerHeader,
  },
  mixins: [AssetSourceMixin, BrandedAssetMixin],
  props: {
    caseStudy: { type: Object, required: true },
    editable: { type: Boolean, required: false, default: false },
  },
  async beforeMount() {
    useTestimonialForRecipientStore().fetch(this.caseStudy.recipient_id, this.caseStudy.id)
  },
  methods: {
    // Used with contenteditable to save data to the model
    writeField(event) {
      let el = event.target
      // We expect a name attribute on the editable element which refers
      // to the case study field
      let fieldName = el.getAttribute('name')
      let newContent = el.innerText
      useCaseStudyStore().updateCaseStudy({ ...this.caseStudy, ...{ [fieldName]: newContent } })

      el.blur()
    },
    addSection(type) {
      if (type === 'image') {
        this.$modal.show('file_section_upload_modal')
      } else if (type === 'testimonial') {
        this.$modal.show('testimonial_select_modal')
      } else {
        useCaseStudyStore().createSection(this.caseStudy.id, type)
      }
    },
    // Used with contenteditable to save data to the model
    createTestimonialSection(testimonial) {
      // We assume that the supplied testimonial object is a valid testimonial section, i.e.
      // has correct properties for a testimonial section
      useCaseStudyStore()
        .createSection(this.caseStudy.id, 'testimonial', testimonial)
        .then(() => {
          this.$modal.hide('testimonial_select_modal')
        })
        .then(() => {
          useTestimonialForRecipientStore().fetch(this.caseStudy.recipient_id, this.caseStudy.id)
        })
    },
    uploadSuccess(file, response) {
      useCaseStudyStore().updateCaseStudy(response.case_study)
      this.$modal.hide('file_section_upload_modal')
    },
  },
  computed: {
    shortLink() {
      return generateShareLink(this.caseStudy.identifier)
    },
    accentColor() {
      return this.caseStudy.accent_color
    },
    headerBackgroundColor() {
      return this.caseStudy.header_background_color
    },
    editableText() {
      // In Vue 3 you have to use null/undefined to make an attribute conditionally
      // not render, false won't work, so we have this weird extra computed to translate
      // the editable property from true/false into true/null
      return this.editable ? true : null
    },
    imageDropzoneOptions() {
      return {
        url: `/app/api/v2/case_studies/${this.caseStudy.id}/sections/create_image_section.json`,
        headers: { 'X-CSRF-Token': getCsrfToken() },
      }
    },
    hasSnapshot() {
      let snapshotSection = this.caseStudy.sections.find((s) => s.type === 'survey_snapshot')
      return !!snapshotSection
    },
    testimonials() {
      return useTestimonialForRecipientStore().testimonials
    },
    hasTestimonials() {
      return this.testimonials.length > 0
    },
  },
  watch: {
    'caseStudy.sections': {
      handler() {
        useTestimonialForRecipientStore().fetch(this.caseStudy.recipient_id, this.caseStudy.id)
      },
    },
  },
}
</script>
<style lang="sass" scoped>
.testimonial-select li:hover
  background-color: #e9ecef // var(--gray-200)

.main-body
  padding: 24px
  gap: 16px

  &__left-summary
    width: 25%
    max-width: 300px

  &__section-wrapper
    width: 75%

.main-header
  padding: 24px
  min-height: 410px

  &__add-section
    margin-top: -30px

  &__profile-img
    width: 35%
    max-width: 370px

  &__summary
    gap: 40px
    width: 65%
    max-width: 900px

    &__title
      font-size: 48px
      line-height: 60px
      color: black
    &__cta
      font-size: 15px
      color: white
      max-width: 140px
      padding: 8px 6px
      &:hover
        color: white

    &__description
      line-height: 38px
      font-size: 24px

.overflowed-top
  margin-top: -30px
</style>
